import styled from 'styled-components';
import type {TicketSla} from '../types/ticket';
import type {TicketingTokens} from '../../../styles/tokens';

type ColorToken = keyof TicketingTokens['color'];
type SlaVariant = 'paused' | 'breached' | 'warning' | 'ontrack';

const BG: Partial<Record<SlaVariant, ColorToken>> = {
	paused: 'neutralBg',
	breached: 'dangerBg',
	warning: 'warningBg',
};

const BORDER: Partial<Record<SlaVariant, ColorToken>> = {
	breached: 'dangerBorder',
	warning: 'warningBorder',
};

const TEXT: Record<SlaVariant, ColorToken> = {
	paused: 'neutralText',
	breached: 'dangerText',
	warning: 'warningText',
	ontrack: 'onTrackText',
};

const SlaBadgeBase = styled.span<{$variant: SlaVariant}>`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	font: ${p => p.theme.ticketing.font.body3};
	color: ${p => p.theme.ticketing.color[TEXT[p.$variant]]};
	${p => BG[p.$variant] && `background: ${p.theme.ticketing.color[BG[p.$variant]!]};`}
	${p => BG[p.$variant] && `border-radius: ${p.theme.ticketing.radius.full};`}
	${p => BG[p.$variant] && 'padding: 2px 8px;'}
	${p => BG[p.$variant] && 'font-weight: 500;'}
	${p => BORDER[p.$variant] && `border: 1px solid ${p.theme.ticketing.color[BORDER[p.$variant]!]};`}
`;

const SlaIcon = styled.svg`
	height: 12px;
	width: 12px;
`;

export function formatElapsedTime(seconds: number): string {
	const hours = Math.floor(seconds / 3600);
	if (hours >= 24) {
		const days = Math.floor(hours / 24);
		const remainingHours = hours % 24;
		return `${days}d ${remainingHours}h`;
	}
	const minutes = Math.floor((seconds % 3600) / 60);
	return `${hours}h ${minutes}m`;
}

export function SLABadge({ticket}: {ticket: {sla: TicketSla | null | undefined}}) {
	const {sla} = ticket;

	if (!sla) return null;

	if (sla.paused) {
		return (
			<SlaBadgeBase $variant="paused">
				<SlaIcon fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
					<circle cx="12" cy="12" r="10" />
					<path d="M10 9l2 2 4-4" />
				</SlaIcon>
				paused
			</SlaBadgeBase>
		);
	}

	if (sla.breached) {
		return (
			<SlaBadgeBase $variant="breached">
				<SlaIcon fill="currentColor" viewBox="0 0 20 20">
					<circle cx="10" cy="10" r="10" />
				</SlaIcon>
				breached
			</SlaBadgeBase>
		);
	}

	const ratio = sla.thresholdSeconds > 0 ? sla.elapsedSeconds / sla.thresholdSeconds : 0;

	if (ratio >= 0.8) {
		return (
			<SlaBadgeBase $variant="warning">
				<SlaIcon fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
					<circle cx="12" cy="12" r="10" />
					<path d="M12 6v6l4 2" />
				</SlaIcon>
				{formatElapsedTime(sla.elapsedSeconds)}
			</SlaBadgeBase>
		);
	}

	return (
		<SlaBadgeBase $variant="ontrack">
			<SlaIcon fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
				<circle cx="12" cy="12" r="10" />
				<path d="M12 6v6l4 2" />
			</SlaIcon>
			{formatElapsedTime(sla.elapsedSeconds)}
		</SlaBadgeBase>
	);
}
